import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Megaphone, Mail, BellRing } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { timeAgo } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  ComposerDrawer,
  type ComposerDrawerInitial,
} from '../components/announcements/ComposerDrawer';
import { type AnnouncementRecord } from '../components/system/types';
import {
  FilterBar,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  announcementsFilterConfig,
  announcementsSearchSchema,
} from '../components/announcements/announcementsFilterConfig';

// The endpoint caps at 50 rows server-side and is NOT paginated. `useListFilters`
// requires a `pageSize`; passing the server cap is harmless because `page` /
// `pageSize` are ignored by this endpoint (no `page` key in the schema, no
// Pagination control), while `q` / `state` / `publishedFrom·To` / `sort` drive it.
const PAGE_SIZE = 50;

export const Route = createFileRoute('/_authed/announcements')({
  validateSearch: announcementsSearchSchema,
  component: AnnouncementsPage,
});

interface AnnouncementsListResponse {
  items: AnnouncementRecord[];
}

/**
 * Discriminator for the create vs edit composer drawer. `edit` carries the
 * row used to prefill the form; PR 15 will swap the underlying mutation
 * from POST to PATCH. Until then, the edit submit closes with a toast
 * (see `ComposerDrawer` for that branch).
 */
type DrawerState =
  | { kind: 'create' }
  | { kind: 'edit'; initial: ComposerDrawerInitial }
  | null;

function AnnouncementsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const canCompose = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin'));
  const [drawer, setDrawer] = useState<DrawerState>(null);

  // The single source of truth for search / filters / sort, backed by the route's
  // URL search params. The adapter narrows the route's precisely-typed search /
  // navigate surface to the toolkit's structural `FilterRoute` contract; the
  // route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(announcementsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { apiParams } = filters;

  // Whether the empty result is a filtered-out set vs a genuinely empty list —
  // drives filter-aware empty-state copy in both the admin and reader branches.
  const hasActiveFilters =
    filters.activeFilterCount > 0 || filters.searchText.trim().length > 0;

  const listQuery = useQuery({
    queryKey: ['announcements', apiParams] as const,
    queryFn: ({ signal }) =>
      apiRequest<AnnouncementsListResponse>('/api/announcements', {
        signal,
        query: apiParams,
      }),
    placeholderData: (prev) => prev,
  });

  // The endpoint returns `{ items }` only — NOT paginated (it caps at 50 rows
  // server-side), so the result count is `items.length` and there is no
  // Pagination control (there was none before).
  const items = listQuery.data?.items ?? [];

  const columns = useMemo<ColumnDef<AnnouncementRecord, unknown>[]>(() => {
    const base: ColumnDef<AnnouncementRecord, unknown>[] = [
      {
        id: 'title',
        header: 'Title',
        cell: (info) => (
          <span className="font-medium text-[color:var(--color-text)]">
            {info.row.original.title}
          </span>
        ),
      },
      {
        id: 'body',
        header: 'Preview',
        cell: (info) => (
          <span className="text-sm text-[color:var(--color-text-muted)] line-clamp-1">
            {info.row.original.body}
          </span>
        ),
      },
      {
        id: 'channels',
        header: 'Channels',
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="flex items-center gap-2 text-xs text-[color:var(--color-text-muted)]">
              {r.sendPush ? (
                <span className="inline-flex items-center gap-1">
                  <BellRing size={12} aria-hidden="true" /> push
                </span>
              ) : null}
              {r.sendEmail ? (
                <span className="inline-flex items-center gap-1">
                  <Mail size={12} aria-hidden="true" /> email
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1">
                <Megaphone size={12} aria-hidden="true" /> in-app
              </span>
            </div>
          );
        },
      },
      {
        id: 'publishedAt',
        header: 'Published',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {info.row.original.publishedAt
              ? timeAgo(info.row.original.publishedAt)
              : 'Draft'}
          </span>
        ),
      },
    ];

    if (canCompose) {
      base.push({
        id: 'edit',
        header: '',
        cell: (info) => (
          <div className="flex items-center justify-end">
            <EditIconButton
              onClick={() =>
                setDrawer({ kind: 'edit', initial: info.row.original })
              }
              label={`Edit ${info.row.original.title}`}
              title="Edit announcement"
            />
          </div>
        ),
      });
    }

    return base;
  }, [canCompose]);

  return (
    <ListOrOutlet basePath="/announcements">
    <ListShell
      title="Announcements"
      description="Reach all users, a role, a batch, or a family."
      primaryAction={
        canCompose ? (
          <Button
            onClick={() => setDrawer({ kind: 'create' })}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            New announcement
          </Button>
        ) : null
      }
    >
      {/*
        Mounted ONCE above BOTH branches: the backend applies the same list
        filters (q / state / published range / sort) to admins and targeted
        readers alike, so the bar behaves identically for every role.
      */}
      <FilterBar
        config={announcementsFilterConfig}
        filters={filters}
        resultCount={items.length}
      />

      {listQuery.isError ? (
        <ErrorState
          title="Could not load announcements"
          description={
            listQuery.error instanceof ApiError
              ? listQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => listQuery.refetch()}
        />
      ) : canCompose ? (
        <DataTable<AnnouncementRecord>
          data={items}
          columns={columns}
          isLoading={listQuery.isLoading && !listQuery.data}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            void navigate({
              to: '/announcements/$announcementId',
              params: { announcementId: row.id },
            })
          }
          emptyState={
            hasActiveFilters ? (
              <EmptyState
                title="No announcements match these filters"
                description="Try clearing a filter or adjusting the date range."
                icon={<Megaphone size={32} aria-hidden="true" />}
              />
            ) : (
              <EmptyState
                title="No announcements yet"
                description="Reach the whole academy or a focused audience with a single message."
                icon={<Megaphone size={32} aria-hidden="true" />}
                action={
                  <Button
                    onClick={() => setDrawer({ kind: 'create' })}
                    icon={<Plus size={16} aria-hidden="true" />}
                  >
                    New announcement
                  </Button>
                }
              />
            )
          }
        />
      ) : (
        <ReadOnlyList
          items={items}
          isLoading={listQuery.isLoading && !listQuery.data}
          hasActiveFilters={hasActiveFilters}
        />
      )}

      <ComposerDrawer
        open={drawer !== null}
        onClose={() => setDrawer(null)}
        initial={drawer?.kind === 'edit' ? drawer.initial : null}
        onPublished={() => {
          void queryClient.invalidateQueries({ queryKey: ['announcements'] });
          toast('Announcement published.', { variant: 'success' });
          setDrawer(null);
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

// ---------- Read-only list (non-admin roles) ----------

function ReadOnlyList({
  items,
  isLoading,
  hasActiveFilters,
}: {
  items: ReadonlyArray<AnnouncementRecord>;
  isLoading: boolean;
  hasActiveFilters: boolean;
}): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (items.length === 0) {
    return hasActiveFilters ? (
      <EmptyState
        title="No announcements match these filters"
        description="Try clearing a filter or adjusting the date range."
        icon={<Megaphone size={32} aria-hidden="true" />}
      />
    ) : (
      <EmptyState
        title="No announcements yet"
        description="Anything the academy shares with you will appear here."
        icon={<Megaphone size={32} aria-hidden="true" />}
      />
    );
  }
  return (
    <>
      <ul className="space-y-3" role="list">
        {items.map((item) => (
          <li key={item.id}>
            <Link
              to="/announcements/$announcementId"
              params={{ announcementId: item.id }}
              className="block rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4 no-underline transition-colors duration-base hover:border-gold-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-display text-base font-semibold text-[color:var(--color-text)]">
                  {item.title}
                </h3>
                <span className="shrink-0 text-xs text-[color:var(--color-text-muted)]">
                  {item.publishedAt ? timeAgo(item.publishedAt) : 'Draft'}
                </span>
              </div>
              <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
                {item.body}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
